import React, {useEffect, useState} from "react";
import {
    Box,
    Button,
    useDisclosure,
    Flex,
    Text, Tag, TagLabel, TagLeftIcon, IconButton, HStack, Spinner,
} from "@chakra-ui/react";
import {Calendar, momentLocalizer, Views} from "react-big-calendar";
import moment from "moment";
import 'moment/locale/de';
import "react-big-calendar/lib/css/react-big-calendar.css";
import {deleteData, getData} from "../../../services/api";
import {RENTAL_STATUS} from "../../../variables/rentalStatus";
import {FaEuroSign, FaPlus, FaClock, FaInfinity, FaCheck, FaTimes} from "react-icons/fa";
import RentalSelect from "../../../components/rental/RentalSelect";
import AddDateModal from "../../../components/modals/AddDateModal";
import AddLastMinuteModal from "../../../components/modals/AddLastMinuteModal";
import AddPriceModal from "../../../components/modals/AddPriceModal";
import AddCalendarModal from "../../../components/modals/AddCalendarModal";

const localizer = momentLocalizer(moment);
const EVENT_TYPE_COLORS = {'misc': "gray", 'available': "green", 'blocked': "red", 'lastminute': "brand"};

export default function UserCalendar() {

    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [rentals, setRentals] = useState([]);
    const [selectedRental, setSelectedRental] = useState(null);
    const [rentalDates, setRentalDates] = useState([]);

    const {
        isOpen: isAddDateOpen,
        onOpen: onAddDateOpen,
        onClose: onAddDateClose,
    } = useDisclosure();

    const {
        isOpen: isAddLastMinuteOpen,
        onOpen: onAddLastMinuteOpen,
        onClose: onAddLastMinuteClose,
    } = useDisclosure();

    const {
        isOpen: isAddCalendarOpen,
        onOpen: onAddCalendarOpen,
        onClose: onAddCalendarClose,
    } = useDisclosure();

    const {
        isOpen: isAddPriceOpen,
        onOpen: onAddPriceOpen,
        onClose: onAddPriceClose,
    } = useDisclosure();

    const fetchRentalDates = async () => {
        if (!selectedRental) return;

        try {
            setLoading('dates');
            const result = await getData(`/api/rentals/${selectedRental.id}/dates`);
            setRentalDates(result);
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {

        const fetchRentals = async () => {
            try {
                setLoading(rentals);
                const result = await getData('/api/rentals');
                setRentals(result.data);
                if (result.data.length > 0) {
                    setSelectedRental(result.data?.filter(rental => rental.status === RENTAL_STATUS.ONLINE)[0]);
                }
                setLoading(false);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchRentals();
    }, []);

    useEffect(() => {
        fetchRentalDates();
    }, [selectedRental]);

    function handleRentalDateAdded(date) {
        setRentalDates((prev) => [...prev, date]);
    }

    function handleIcalImported(url) {
        setSelectedRental(prev => ({
            ...prev,
            ical: url,
        }));
    }

    const handleSelectEvent = async (event) => {

        const formattedStart = moment(event.start).format('DD.MM.YYYY');
        const formattedEnd = moment(event.end).format('DD.MM.YYYY');

        if (window.confirm(`Möchtest du den Termin "${event.title}" vom ${formattedStart} - ${formattedEnd} löschen?`)) {
            try {
                await deleteData(`/api/rentals/${selectedRental.id}/dates/${event.resource.id}`);
                setRentalDates((prev) => prev.filter(d => d.id !== event.resource.id));
            } catch (error) {
                console.error("Fehler beim Löschen:", error);
            }
        }
    };

    const handleRemoveIcal = async (rentalId) => {
        try {
            setLoading('ical');
            await deleteData('/api/rentals/' + rentalId + '/ical');
            setSelectedRental({...selectedRental, ical: ''});
            setLoading(false);
        } catch (error) {
            console.error("Fehler beim Löschen:", error);
        }
    }

    const mappedDates = rentalDates?.map((item) => ({
        title: item.title || 'Unbekannt',
        status: item.status,
        start: new Date(item.date_from),
        end: new Date(item.date_to),
        allDay: true,
        type: item.type,
        resource: item,
    }));

    return (
        <Box pt={"80px"}>
            <Text mb={5}>Hier kannst du für deine Unterkünfte Belegungen, Last-Minute-Angebote und Saisonpreise
                hinzufügen sowie deinen Buchungskalender verknüpfen.</Text>

            <Flex mb={5} alignItems={'center'} gap={3}>
                <Text><strong>Gewählte Unterkunft:</strong></Text>
                <RentalSelect
                    rentals={rentals?.filter(rental => rental.status === RENTAL_STATUS.ONLINE)}
                    value={selectedRental}
                    onSelect={(rental) => setSelectedRental(rental)}
                />
            </Flex>

            {selectedRental &&
                <Box mb={5}>

                    <Flex gap={3} mb={3} alignItems={'center'}>
                        <Button onClick={onAddCalendarOpen} variant={'outline'} colorScheme={'brand'}><FaInfinity/><Text
                            ml={2}>Buchungskalender
                            verknüpfen (iCal)</Text></Button>
                        {selectedRental && selectedRental.ical &&
                            <HStack>
                                <Tag title={selectedRental.ical} width={300} borderRadius='full' variant='outline'
                                     colorScheme='green'>
                                    <TagLeftIcon as={FaCheck}/>
                                    <TagLabel>Verknüpft mit: {selectedRental.ical}</TagLabel>
                                </Tag>
                                {loading === 'ical' ?
                                    <Spinner size="sm"/> :
                                    <IconButton
                                        aria-label="Verknüpfung entfernen"
                                        title="Verknüpfung entfernen"
                                        icon={<FaTimes/>}
                                        colorScheme="red"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleRemoveIcal(selectedRental.id)}
                                    />}
                            </HStack>
                        }
                    </Flex>
                    <Flex gap={3}>
                        <Button onClick={onAddDateOpen} variant={'solid'} colorScheme={'brand'}><FaPlus/><Text ml={2}>Belegten
                            Termin
                            hinzufügen</Text></Button>
                        <Button onClick={onAddLastMinuteOpen} variant={'solid'} colorScheme={'brand'}><FaClock/><Text
                            ml={2}>Last
                            Minute hinzufügen</Text></Button>
                        <Button onClick={onAddPriceOpen} variant={'solid'} colorScheme={'brand'}><FaEuroSign/><Text
                            ml={2}>Saisonpreis
                            hinzufügen</Text></Button>
                    </Flex>
                </Box>
            }

            {selectedRental &&
                <Calendar
                    localizer={localizer}
                    events={mappedDates}
                    startAccessor="start"
                    endAccessor="end"
                    style={{height: 760}}
                    views={[Views.MONTH, Views.DAY]}
                    eventPropGetter={event => {
                        return {
                            style: {backgroundColor: EVENT_TYPE_COLORS[event.type]} // fallback
                        };
                    }}
                    onSelectEvent={handleSelectEvent}
                    messages={{
                        next: "Nächste",
                        previous: "Vorherige",
                        today: "Heute",
                        month: "Monat",
                        week: "Woche",
                        day: "Tag",
                        agenda: "Agenda",
                        date: "Datum",
                        time: "Uhrzeit",
                        event: "Termin",
                        noEventsInRange: "Keine Termine in diesem Zeitraum",
                        showMore: function showMore(total) {
                            return "+" + total + (total > 1 ? " weitere Einträge" : " weiterer Eintrag");
                        }
                    }}
                />
            }

            <AddDateModal
                isOpen={isAddDateOpen}
                onClose={onAddDateClose}
                selectedRental={selectedRental}
                onRentalDateAdded={handleRentalDateAdded}
            />

            <AddLastMinuteModal
                isOpen={isAddLastMinuteOpen}
                onClose={onAddLastMinuteClose}
                selectedRental={selectedRental}
                onRentalDateAdded={handleRentalDateAdded}
            />

            <AddPriceModal
                isOpen={isAddPriceOpen}
                onClose={onAddPriceClose}
                selectedRental={selectedRental}
                onRentalDateAdded={handleRentalDateAdded}
            />

            <AddCalendarModal
                isOpen={isAddCalendarOpen}
                onClose={onAddCalendarClose}
                selectedRental={selectedRental}
                onIcalImported={handleIcalImported}
            />
        </Box>
    );
}
